import { useEffect, useState } from "react";

import { AssistantScope } from "@app_schema";

import { useIsWide } from "@application/hooks/use_is_wide";

import { type PageTarget } from "../../projects/assistant/page_tag";
import { useAssistantChats } from "../../projects/assistant/use_assistant_chats";
import { useAssistantNotes } from "../../projects/assistant/use_assistant_notes";

export type RecordsAssistantConfig = {
  open: boolean;
  projectId: string;
  agentId?: string;
};

export type NarrowPane = "assistant" | "letters";

// The Records assistant inside the records split: its chats, its notes, the
// page a tag opened, and (under 1280) which pane shows.
export const useRecordsAssistant = (config?: RecordsAssistantConfig) => {
  const open = config?.open ?? false;
  const chats = useAssistantChats({
    projectId: config?.projectId ?? "",
    scope: AssistantScope.Records,
    agentId: config?.agentId,
    open,
  });
  const notes = useAssistantNotes({
    projectId: config?.projectId ?? "",
    chats,
    open,
  });
  const isWide = useIsWide();
  const [openPage, setOpenPage] = useState<PageTarget | null>(null);
  // The notes opening, by hand or following the assistant, close a page a
  // tag opened: they take the letters' place only with no page open.
  useEffect(() => {
    if (notes.shown) setOpenPage(null);
  }, [notes.shown]);
  const [narrowPane, setNarrowPane] = useState<NarrowPane>("assistant");

  return {
    open,
    agentReady: Boolean(config?.agentId),
    chats,
    notes,
    isWide,
    openPage: open ? openPage : null,
    // The notes take the letters' place only while the letters would show.
    notesOver:
      open &&
      notes.shown &&
      openPage === null &&
      (isWide || narrowPane === "letters"),
    setOpenPage,
    narrowPane,
    setNarrowPane,
  } as const;
};

export type RecordsAssistant = ReturnType<typeof useRecordsAssistant>;
